import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Paper
} from "@mui/material";

import Draggable from "react-draggable";
import DownloadIcon from "@mui/icons-material/Download";
import SettingsIcon from "@mui/icons-material/Settings";
import CloseIcon from "@mui/icons-material/Close";

import FileInput from "../FileInput/FileInput";
import PermissionsModal from "./PermissionsModal";
import { AxiosService } from "../../Services/AxiosService";
import { useAuth } from "react-oidc-context";

/**
 * Draggable modal wrapper
 */
function PaperComponent(props) {
  const nodeRef = React.useRef(null);

  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper
        {...props}
        ref={nodeRef}
        className="formPaper"
        sx={{
          overflow: "auto",
          position: "fixed",
          top: "90px",
          maxWidth: "460px",
          borderRadius: "30px"
        }}
      />
    </Draggable>
  );
}

function FileModal({ open, onClose, rowData }) {
  const auth = useAuth();
  const token = auth.user?.access_token;

  const [files, setFiles] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);

  const [openPermissions, setOpenPermissions] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState(null);

  /**
   * FETCH FILES
   */
    const fetchFiles = React.useCallback(async () => {
      if (!rowData?.subprocessId) return;

      try {
        setLoading(true);

        const response = await AxiosService.subprocess_files(
          token,
          rowData.subprocessId
        );

        setFiles(response.data);

      } catch (err) {
        console.error("Error fetching files:", err);
        setFiles([]);
      } finally {
        setLoading(false);
      }
    }, [rowData?.subprocessId, token]);

    React.useEffect(() => {
      if (open) {
        fetchFiles();
      }
    }, [open, fetchFiles]);

  /**
   * DOWNLOAD FILE
   */
    const handleDownload = async (file) => {
      try {
        const response = await AxiosService.download_file(token, file.id);

        const url = response.data.url;

        window.open(url, "_blank");

      } catch (err) {
        console.error("Download error:", err);
      }
    };

  /**
   * UPLOAD FILE
   */
  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!rowData?.subprocessId) {
      console.error("Missing subprocessId");
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("file", file);

      const response = await AxiosService.post_file(
        token,
        rowData.subprocessId,
        formData
      );

      const createdFile = response.data;

      setSelectedFile(createdFile);
      setOpenPermissions(true);

      fetchFiles();

    } catch (err) {
      console.error("Upload error:", err);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    onClose?.();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          onClose?.();
        }}
        disableEnforceFocus
        disableAutoFocus
        PaperComponent={PaperComponent}
      >
        <DialogTitle id="draggable-dialog-title" sx={{ cursor: "move" }}>
          {rowData?.process} - {rowData?.targetEmployeeName} {rowData?.targetEmployeeSurname}
        </DialogTitle>

        <DialogContent sx={{ maxHeight: "62vh" }}>
          {loading ? (
            <Typography>Loading...</Typography>
          ) : files.length === 0 ? (
            <Typography>No files</Typography>
          ) : (
            <Box>
              {files.map(file => (
                <Box
                  key={file.id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px"
                  }}
                >
                  <Box>
                    <Typography>{file.filename}</Typography>
                    <Typography variant="caption">
                      {file.permissions?.join(", ")}
                    </Typography>
                  </Box>

                  <Box>
                    <IconButton onClick={() => handleDownload(file)}>
                      <DownloadIcon />
                    </IconButton>

                    <IconButton
                      onClick={() => {
                        setSelectedFile(file);
                        setOpenPermissions(true);
                      }}
                    >
                      <SettingsIcon />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <FileInput handleSelect={handleUpload} />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>
            <CloseIcon /> Exit
          </Button>
        </DialogActions>
      </Dialog>

      <PermissionsModal
        open={openPermissions}
        onClose={() => setOpenPermissions(false)}
        file={selectedFile}
        onSaved={fetchFiles}
      />
    </>
  );
}

export default FileModal;